import { ApolloClient, InMemoryCache, createHttpLink, from } from '@/apollo'
import { ErrorLink } from '@apollo/client/link/error'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { RetryLink } from '@apollo/client/link/retry'
import { Observable } from 'rxjs'
import { getGraphQLEndpoint } from '../utils/graphqlConfig'

const graphqlUri = getGraphQLEndpoint()

const httpLink = createHttpLink({
  uri: graphqlUri,
  credentials: 'include',
})

const REFRESH_MUTATION = `
  mutation RefreshSession {
    refreshToken {
      success
    }
  }
`

let refreshInFlight: Promise<boolean> | null = null

async function refreshSessionFromCookies(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  refreshInFlight = (async () => {
    try {
      const response = await fetch(graphqlUri, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query: REFRESH_MUTATION }),
      });

      if (!response.ok) {
        return false;
      }

      const result = await response.json();
      return result.data?.refreshToken?.success === true;
    } catch {
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

const errorLink = new ErrorLink(({ error, operation, forward }) => {
  if (CombinedGraphQLErrors.is(error)) {
    const isUnauthenticated = error.errors.some(
      (graphQLError) =>
        graphQLError.extensions?.['code'] === 'UNAUTHENTICATED' ||
        graphQLError.message.includes('Authentication required') ||
        graphQLError.message.includes('Token expired')
    );

    if (
      isUnauthenticated &&
      operation.operationName !== 'RefreshSession' &&
      operation.operationName !== 'Login' &&
      operation.operationName !== 'LoginWithEntra' &&
      operation.operationName !== 'VerifyLoginMfa'
    ) {
      // ErrorLink requires an Observable, not a Promise (Apollo Client 4).
      return new Observable((subscriber) => {
        let innerSub: { unsubscribe: () => void } | undefined
        let cancelled = false

        refreshSessionFromCookies()
          .then((refreshed) => {
            if (cancelled) return
            if (refreshed) {
              innerSub = forward(operation).subscribe(subscriber)
              return
            }
            subscriber.error(error)
          })
          .catch((refreshError) => {
            if (!cancelled) subscriber.error(refreshError)
          })

        return () => {
          cancelled = true
          innerSub?.unsubscribe()
        }
      })
    }

    error.errors.forEach(({ message, locations, path }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
    })
    return
  }

  console.error(`[Network error]: ${error}`)
  return
})

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 3000,
    jitter: true
  },
  attempts: {
    max: 3,
    retryIf: (error, operation) => {
      if (
        operation.operationName === 'LoginWithEntra' ||
        operation.operationName === 'Login' ||
        operation.operationName === 'VerifyLoginMfa' ||
        operation.operationName === 'CreateUser'
      ) {
        return false;
      }

      if (error && 'statusCode' in error && typeof error.statusCode === 'number' && error.statusCode >= 400 && error.statusCode < 500) {
        return false;
      }
      return !!error;
    },
  }
})

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        providerProfiles: {
          merge(_existing = [], incoming: any) {
            return incoming
          }
        },
        providers: {
          merge(_existing = [], incoming: any) {
            return incoming
          }
        },
        applicationSettings: {
          read() {
            return undefined;
          }
        }
      }
    },
    ApplicationSettings: {
      keyFields: false
    }
  }
})

if (typeof window !== 'undefined') {
  const cacheVersionKey = 'apollo-cache-version';
  localStorage.removeItem('apollo-cache-persist');
  localStorage.removeItem(cacheVersionKey);
}

export const apolloClient = new ApolloClient({
  link: from([errorLink, retryLink, httpLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
  devtools: {
    enabled: (import.meta as any).env.DEV
  }
})

export default apolloClient
